'use client'
import { useRef, useEffect, useState } from "react";
import { createClient } from "@/prismicio";
import { PrismicRichText, PrismicLink } from "@prismicio/react";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import { isFilled } from "@prismicio/client";

// Composant pour un article
const BlogArticleCard = ({ article, featured = false }) => {
  const imageUrl = article.data.preview_image?.url;
  const imageAlt = article.data.preview_image?.alt || article.data.preview_title?.[0]?.text || "Image de l'article";
  const date = isFilled.date(article.data.preview_date)
    ? new Date(article.data.preview_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'numeric', day: 'numeric' })
    : null;
  const tag = article.tags?.[0];

  return (
    <article
      className={`bg-white w-full h-full flex flex-col md:flex-row ${featured ? "min-h-90" : ""}`}
      itemScope
      itemType="https://schema.org/BlogPosting"
    >
      <div
        className={`relative bg-cover bg-center bg-no-repeat min-h-50 ${featured ? "md:w-[40%] lg:w-[45%]" : "md:w-[45%] lg:w-[35%]"} w-full`}
        style={{ backgroundImage: `url(${imageUrl})` }}
      >
        <img
          src={imageUrl}
          alt={imageAlt}
          className="absolute inset-0 w-full h-full object-cover opacity-0"
          itemProp="image"
        />
        {featured && (
          <img src="/y-img.svg" alt="image de décoration" className="absolute z-[10] h-full w-auto object-contain top-0 -right-[2px]" />
        )}
      </div>
      <div className={`${featured ? "md:w-[60%] lg:w-[55%]" : "md:w-[55%] lg:w-[65%]"} w-full p-8 md:pl-5 md:pr-15 flex flex-col my-auto`}>
        {date && (
          <p className="text-date mb-2" itemProp="datePublished">{date}</p>
        )}
        <div className={`${featured ? "line-clamp-7 md:line-clamp-5" : "line-clamp-6"} mb-10`}>
          {isFilled.richText(article.data.preview_title) && (
            <PrismicRichText
              field={article.data.preview_title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="h3-sm normal-case mb-2.5" itemProp="headline">{children}</h2>
                ),
                heading3: ({ children }) => (
                  <h2 className="h3-sm normal-case mb-2.5" itemProp="headline">{children}</h2>
                ),
              }}
            />
          )}
          {isFilled.richText(article.data.preview_description) && (
            <PrismicRichText
              field={article.data.preview_description}
              components={{
                paragraph: ({ children }) => (
                  <p itemProp="description">{children}</p>
                ),
              }}
            />
          )}
        </div>
        <div className="flex justify-between items-end">
          {tag && (
            <span className="text-tags tag-green text-white" itemProp="keywords">{tag}</span>
          )}
          <div className="flex-1 text-end">
            <PrismicLink
              document={article}
              className="p-sm inline-block py-1"
              aria-label={`Lire plus sur ${article.data.preview_title?.[0]?.text || "l'article"}`}
              itemProp="url"
            >
              Lire plus
            </PrismicLink>
          </div>
        </div>
      </div>
    </article>
  );
};

const BlogLatestAndList = ({ slice, context, index }) => {
  const { title, title_articles, featured_article, title_newsletter, rgpd } = slice.primary;
  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(1);
  const articlesPerPage = 16;
  const [selectedTag, setSelectedTag] = useState("");
  const listRef = useRef(null);
  const [hasMounted, setHasMounted] = useState(false);
  
  // États pour le formulaire newsletter
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Fonction pour gérer la soumission du formulaire newsletter
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !privacyAccepted || isLoading) return;
    
    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setEmail('');
        setPrivacyAccepted(false);
      } else {
        setMessage(data.error || 'Une erreur est survenue');
      }
    } catch (error) {
      setMessage('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      const client = createClient();
      const response = await client.getAllByType("article");
      const visibles = response.filter(article => article.data.visible === true);

      // Exclure l'article à la une
      const filtered = isFilled.contentRelationship(featured_article)
        ? visibles.filter(article => article.id !== featured_article.id)
        : visibles;

      setArticles(filtered);
    };
    fetchArticles();
  }, [featured_article]);

  useEffect(() => {
    if (hasMounted && listRef.current) {
      listRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [page]);

  // Récupérer tous les tags uniques
  const allTags = Array.from(new Set(articles.flatMap(article => article.tags)));

  // Filtrer les articles selon le tag sélectionné
  const filteredArticles = selectedTag
    ? articles.filter(article => article.tags.includes(selectedTag))
    : articles;

  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const paginatedArticles = filteredArticles.slice(
    (page - 1) * articlesPerPage,
    page * articlesPerPage
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={` ${index == 0 ? 'pb-30' : 'py-16 md:py-23 '} bg-light-gray framing text-dark-green`}
      aria-label="Section blog"
    >
      <div className="max-w-350 mx-auto">
        <div>
          <Breadcrumbs context={context} index={index} textcolor={"text-dark-green"} />
          <div className="flex flex-col gap-20 lg:gap-30">
            <div className={` ${index == 0 ? 'md:pt-5' : 'py-0'}`}>
              <div className="flex flex-col gap-12">
                <div className="max-w-200">
                  {isFilled.richText(title) && (
                    <PrismicRichText
                      field={title}
                      components={{
                        heading1: ({ children }) => (
                          <h1 className="h1" itemProp="name">{children}</h1>
                        ),
                        heading2: ({ children }) => (
                          <h2 className="h1" itemProp="name">{children}</h2>
                        ),
                      }}
                    />
                  )}
                </div>
                <div>
                  {isFilled.contentRelationship(featured_article) && (
                    <BlogArticleCard article={featured_article} featured />
                  )}
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col gap-10 " ref={listRef}>
              {isFilled.richText(title_articles) && (
                <PrismicRichText
                  field={title_articles}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="h3">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="h3">{children}</h3>
                    ),
                  }}
                />
              )}
              <div className="flex flex-col gap-10 lg:gap-20">
                <nav className="w-full flex flex-wrap gap-5" aria-label="Filtrer par tag">
                  <div className="w-full md:w-1/2 lg:hidden">
                    <select
                      value={selectedTag}
                      onChange={e => { setSelectedTag(e.target.value); setPage(1); }}
                      className="w-full"
                      aria-label="Filtrer les articles par tag"
                    >
                      <option value="">Tous les articles</option>
                      {allTags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>
                  <div className="hidden lg:flex flex-wrap gap-5">
                    <div
                      onClick={() => { setSelectedTag(""); setPage(1); }}
                      className={`cursor-pointer tag-filter ${selectedTag === "" ? "bg-dark-green text-white" : "bg-transparent text-dark-green"}`}
                    >
                      Tous les articles
                    </div>
                    {allTags.map(tag => (
                      <div
                        key={tag}
                        onClick={() => { setSelectedTag(tag); setPage(1); }}
                        className={`cursor-pointer tag-filter ${selectedTag === tag ? "bg-dark-green text-white" : "bg-transparent text-dark-green"}`}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                </nav>
                <div className="flex flex-col lg:flex-row gap-20 justify-between">
                  <div className="flex flex-col gap-10 w-full">
                    {paginatedArticles.map((article, i) => (
                      <BlogArticleCard key={article.id || i} article={article} />
                    ))}
                  </div>
                  <aside className="lg:w-130" aria-label="Newsletter">
                    <div className="bg-green text-white p-8">
                      <div className="mb-4">
                        {isFilled.richText(title_newsletter) && (
                          <PrismicRichText
                            field={title_newsletter}
                            components={{
                              heading3: ({ children }) => (
                                <h3 className="h3-sm">{children}</h3>
                              ),
                              heading4: ({ children }) => (
                                <h4 className="h3-sm">{children}</h4>
                              ),
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row flex-wrap gap-2.5 md:gap-4">
                          <input
                            type="email"
                            name="email"
                            placeholder="Ton adresse e-mail"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading}
                            className="w-full md:w-2/3 lg:w-full"
                            aria-label="Adresse e-mail"
                          />
                          <label className="md:order-3 xl:order-2">
                            <input 
                              type="checkbox" 
                              name="privacy" 
                              required 
                              checked={privacyAccepted}
                              onChange={(e) => setPrivacyAccepted(e.target.checked)}
                              disabled={isLoading}
                            />
                            {isFilled.richText(rgpd) && (
                              <PrismicRichText
                                field={rgpd}
                                components={{
                                  paragraph: ({ children }) => (
                                    <span className="p-sm">{children}</span>
                                  ),
                                }}
                              />
                            )}
                          </label>
                          <button 
                            type="submit" 
                            disabled={isLoading || !email || !privacyAccepted}
                            className="cta-text mt-5 px-5 w-full md:mt-0 md:order-2 xl:order-3 md:flex-1 xl:flex-none xl:px-20 xl:mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isLoading ? 'EN COURS...' : 'VALIDER'}
                          </button>
                        </form>
                        
                        {message && (
                          <div className={`mt-4 p-3 w-full rounded-lg text-sm ${message.includes('réussi') || message.includes('inscrit') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {message}
                          </div>
                        )}
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            </div>
            {/* Pagination numérotée */}
            {totalPages > 1 && (
              <nav className="flex gap-2 mt-6 justify-center" aria-label="Pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPage(i + 1)}
                    className={`w-8 rounded-full aspect-square cursor-pointer transition-all text-pagination text-dark-green ${page === i + 1 ? 'bg-yellow' : 'bg-transparent'}`}
                    aria-current={page === i + 1 ? "page" : undefined}
                    aria-label={`Page ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </nav>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogLatestAndList;